import { useState, useCallback, useEffect, useRef } from 'react';

type FormatStyle = 'bold' | 'italic' | 'strike';

interface UseTextSelectionToolbarOptions {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  text: string;
  setText: (value: string) => void;
}

interface TextSelectionToolbarState {
  visible: boolean;
  position: { x: number; y: number };
  applyFormat: (style: FormatStyle) => void;
}

/**
 * Format markers matching WhatsApp / legacy makeTextStyle():
 *   bold    → *text*
 *   italic  → _text_
 *   strike  → ~text~
 */
const MARKERS: Record<FormatStyle, string> = {
  bold: '*',
  italic: '_',
  strike: '~',
};

/**
 * Shared hook for text selection formatting toolbar.
 *
 * When user selects text in the textarea, shows a floating toolbar
 * positioned near the selection. Provides `applyFormat()` to wrap
 * the selected text with WhatsApp-style formatting markers.
 */
export function useTextSelectionToolbar({
  textareaRef,
  text,
  setText,
}: UseTextSelectionToolbarOptions): TextSelectionToolbarState {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // Keep a ref to the latest text so the mouseup handler always sees current value
  const textRef = useRef(text);
  textRef.current = text;

  const checkSelection = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      setVisible(false);
      return;
    }

    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selectedText = textRef.current.substring(start, end).trim();

    if (selectedText.length > 0) {
      // Position the toolbar above the textarea, centered
      const rect = el.getBoundingClientRect();
      // Approximate x position: center of textarea
      const x = rect.left + rect.width / 2;
      // Position above the textarea top edge
      const y = rect.top - 8;
      setPosition({ x, y });
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [textareaRef]);

  // Listen for mouseup on textarea to detect selection
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    const handleMouseUp = () => {
      // Small delay to let browser finalize selection
      requestAnimationFrame(() => checkSelection());
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Check selection on shift+arrow keys
      if (e.shiftKey) {
        requestAnimationFrame(() => checkSelection());
      }
    };

    // Hide toolbar when clicking outside or when textarea loses focus
    const handleBlur = (e: FocusEvent) => {
      // Don't hide if clicking the toolbar itself
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (relatedTarget?.closest('[data-formatting-toolbar]')) return;
      // Small delay to allow toolbar button clicks to register
      setTimeout(() => setVisible(false), 150);
    };

    el.addEventListener('mouseup', handleMouseUp);
    el.addEventListener('keyup', handleKeyUp);
    el.addEventListener('blur', handleBlur);

    return () => {
      el.removeEventListener('mouseup', handleMouseUp);
      el.removeEventListener('keyup', handleKeyUp);
      el.removeEventListener('blur', handleBlur);
    };
  }, [textareaRef, checkSelection]);

  const applyFormat = useCallback(
    (style: FormatStyle) => {
      const el = textareaRef.current;
      if (!el) return;

      const start = el.selectionStart;
      const end = el.selectionEnd;
      const currentText = textRef.current;
      const selectedText = currentText.substring(start, end).trim();

      if (!selectedText.length) return;

      const marker = MARKERS[style];

      // Add spaces around markers if adjacent characters aren't spaces (legacy behavior)
      const prevChar = start > 0 ? currentText[start - 1] : ' ';
      const nextChar = end < currentText.length ? currentText[end] : ' ';
      const spaceBefore = prevChar === ' ' || prevChar === '\n' ? '' : ' ';
      const spaceAfter = nextChar === ' ' || nextChar === '\n' ? '' : ' ';

      const formatted = `${spaceBefore}${marker}${selectedText}${marker}${spaceAfter}`;
      const newText = currentText.substring(0, start) + formatted + currentText.substring(end);

      setText(newText);
      setVisible(false);

      // Restore focus and set cursor after the formatted text
      requestAnimationFrame(() => {
        const newCursorPos = start + formatted.length;
        el.selectionStart = el.selectionEnd = newCursorPos;
        el.focus();
      });
    },
    [textareaRef, setText]
  );

  return { visible, position, applyFormat };
}
